import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import api from "@/lib/api";
import { useCatalogLabels } from "@/i18n/catalog";
import {
  ArrowLeft, Truck, Package, ExternalLink, MessageCircle,
  FileText, QrCode, User, MapPin, Sparkles, Clock, CheckCircle, AlertTriangle,
  Edit3, Save, X,
} from "lucide-react";
import OrderDocumentsCard from "@/components/order/OrderDocumentsCard";
import { OrderContentDeliverySeller } from "@/components/order/OrderContentDelivery";

const STATUS_LABEL = {
  CREATED: "Bestellt",
  PAYMENT_PENDING: "Zahlung ausstehend",
  PAID: "Bezahlt",
  AWAITING_INBOUND: "Wartet auf Hub-Eingang",
  INBOUND_RECEIVED: "Im Hub eingegangen",
  SHIPPED: "Versendet",
  DELIVERED: "Zugestellt",
  RECEIVED: "Erhalten",
  COMPLETED: "Abgeschlossen",
  CANCELLED: "Storniert",
  REFUNDED: "Erstattet",
};

const STATUS_COLOR = {
  CREATED: "text-amber-600 bg-amber-50 border-amber-200",
  PAID: "text-emerald-700 bg-emerald-50 border-emerald-200",
  AWAITING_INBOUND: "text-blue-700 bg-blue-50 border-blue-200",
  INBOUND_RECEIVED: "text-blue-700 bg-blue-50 border-blue-200",
  SHIPPED: "text-blue-700 bg-blue-50 border-blue-200",
  DELIVERED: "text-emerald-700 bg-emerald-50 border-emerald-200",
  COMPLETED: "text-[#666] bg-[#F5F5F5] border-[#E5E5E5]",
  CANCELLED: "text-red-700 bg-red-50 border-red-200",
  REFUNDED: "text-[#666] bg-[#F5F5F5] border-[#E5E5E5]",
};

/**
 * SellerOrderDetailPage
 * URL: /profile/sales/:orderId
 *
 * Zeigt:
 *  - Artikel (klickbar zurück zum Listing)
 *  - Käufer (STX = user{N}-Alias, MP = display_name)
 *  - Sonderwunsch, Add-ons, Wear-Days, verhandelter Preis
 *  - Status
 *  - Aktionen:
 *      • Versandetikett (PDF) generieren  (nur Hub-Versand)
 *      • QR Beileger (PDF) generieren     (nur Hub-Versand)
 *      • "Ich habe versendet" → status → SHIPPED
 *      • Chat mit Käufer öffnen
 *  Bei DIRECT_TO_BUYER → beide PDF-Buttons ausgegraut ("Coming Soon").
 */
export default function SellerOrderDetailPage() {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const { addon: addonLabel } = useCatalogLabels();
  const [addonLabels, setAddonLabels] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stilltouchedx/catalog/addons");
        const m = {}; for (const a of (data.addons || [])) m[a.code] = a.label;
        setAddonLabels(m);
      } catch { /* Fallback auf Roh-Code */ }
    })();
  }, []);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [editTracking, setEditTracking] = useState(false);
  const [trackForm, setTrackForm] = useState({ tracking_carrier: "", tracking_number: "" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: o } = await api.get(`/orders/${orderId}`);
        setOrder(o);
        if (o.listing_id) {
          try {
            const { data: l } = await api.get(`/listings/${o.listing_id}`);
            setListing(l);
          } catch { /* Listing evtl. gelöscht */ }
        }
      } catch { setOrder(null); }
      finally { setLoading(false); }
    })();
  }, [orderId]);

  const isHubShipping = ["ANONYMOUS_HUB", "HUB_ANONYMOUS"].includes(order?.shipping_mode);
  const currency = order?.currency || "EUR";

  const openPdf = async (endpoint, filenameHint) => {
    setBusy(endpoint); setError("");
    try {
      const resp = await api.post(`/seller/orders/${orderId}/${endpoint}`, {}, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = `${filenameHint}-${orderId.slice(0, 12)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      // API returned blob for PDF, but on error the body might be JSON — try to parse
      let msg = "PDF konnte nicht erstellt werden.";
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          msg = j?.detail?.message || j?.detail || msg;
        } catch { /* fallback */ }
      } else {
        msg = e.response?.data?.detail?.message || e.response?.data?.detail || msg;
      }
      setError(msg);
    } finally {
      setBusy(null);
    }
  };

  const markShipped = async () => {
    setBusy("ship"); setError("");
    try {
      await api.post(`/orders/${orderId}/ship`);
      const { data: o } = await api.get(`/orders/${orderId}`);
      setOrder(o);
    } catch (e) {
      setError(e.response?.data?.detail || "Konnte nicht als versendet markiert werden.");
    } finally {
      setBusy(null);
    }
  };

  const startEditTracking = () => {
    setTrackForm({
      tracking_carrier: (isHubShipping ? order.inbound_tracking_carrier : order.tracking_carrier) || "",
      tracking_number: (isHubShipping ? order.inbound_tracking_number : order.tracking_number) || "",
    });
    setEditTracking(true);
    setError("");
  };

  const saveTracking = async () => {
    setBusy("tracking"); setError("");
    try {
      await api.patch(`/seller/orders/${orderId}/tracking`, trackForm);
      // Order komplett neu laden — Status/shipped_at können sich mit geändert haben
      const { data: o } = await api.get(`/orders/${orderId}`);
      setOrder(o);
      setEditTracking(false);
    } catch (e) {
      setError(e.response?.data?.detail?.message || e.response?.data?.detail || "Sendungsdaten konnten nicht gespeichert werden.");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
      </div>
    );
  }
  if (!order) {
    return <div className="min-h-full px-4 py-16 text-center text-sm text-[#999]">{t("seller_order_detail_extra.not_found")}</div>;
  }

  const statusPillClass = STATUS_COLOR[order.status] || "text-[#666] bg-[#F5F5F5] border-[#E5E5E5]";
  const buyerLabel = order.buyer_alias || order.buyer_display_name || order.buyer_name || "Käufer";
  const listingHref = listing ? (order.area_scope === "STILLTOUCHEDX" ? `/stilltouchedx/${order.listing_id}` : `/listing/${order.listing_id}`) : null;

  return (
    <div data-testid="seller-order-detail-page" className="min-h-full bg-white text-[#0A0A0A] pb-16">
      {/* Header */}
      <div className="sticky top-0 z-40 flex items-center gap-3 px-4 py-2.5 bg-white border-b border-[#E5E5E5]">
        <button data-testid="seller-order-detail-back" onClick={() => navigate(-1)}
          className="w-9 h-9 -ml-1.5 flex items-center justify-center rounded-full hover:bg-[#F5F5F5]">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold leading-tight truncate">Verkauf</h1>
          <div className="flex items-center gap-1.5 mt-0.5">
            <p data-testid="seller-order-detail-id" className="text-[10px] text-[#888] font-mono truncate">{order.order_id}</p>
            <button
              data-testid="seller-order-copy-id"
              onClick={() => {
                navigator.clipboard.writeText(order.order_id || "");
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
              }}
              className="text-[9px] px-1 py-0.5 border border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5] rounded shrink-0"
              title="Bestellnummer kopieren"
            >
              {copied ? "Kopiert" : "Kopieren"}
            </button>
          </div>
        </div>
        <span data-testid="seller-order-status-pill"
          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border rounded-full ${statusPillClass}`}>
          {STATUS_LABEL[order.status] || order.status}
        </span>
      </div>

      {/* Artikel */}
      <div className="px-4 py-4">
        <button
          data-testid="seller-order-detail-listing-link"
          onClick={() => listingHref && navigate(listingHref)}
          disabled={!listingHref}
          className="w-full flex items-center gap-3 p-3 rounded-xl border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors text-left disabled:opacity-50">
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F5F5F5] shrink-0">
            {listing?.media_urls?.[0] || listing?.title_photo_url ? (
              <img src={listing.title_photo_url || listing.media_urls[0]} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#CCC]">
                <Package size={22} />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate flex items-center gap-1.5">
              {order.sale_mode === "MYSTERY_BAG" && <Sparkles size={12} className="text-amber-500 shrink-0" />}
              {order.title}
            </p>
            <p className="text-[10px] text-[#999] mt-0.5">
              {new Date(order.created_at).toLocaleDateString("de-DE")} · {order.area_scope}
            </p>
            <p className="text-sm font-bold text-[#0A0A0A] mt-1">
              {Number(order.price || 0).toFixed(2)} {currency}
            </p>
          </div>
          {listing && <ExternalLink size={14} className="text-[#999] shrink-0" />}
        </button>
      </div>

      {/* Käufer-Block */}
      <div className="px-4 pb-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("seller_order_detail_extra.buyer")}</p>
        <div className="p-3 border border-[#E5E5E5] rounded-lg flex items-center gap-2 text-sm">
          <User size={14} className="text-[#999]" />
          <span data-testid="seller-order-buyer" className="font-medium">{buyerLabel}</span>
          {order.area_scope === "STILLTOUCHEDX" && (
            <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
              anonym
            </span>
          )}
        </div>
      </div>

      {/* iter174 · Inline-Dokumente-Card (Verkäufer sieht seine Abrechnungen; leer bis Iter B) */}
      <OrderDocumentsCard orderId={orderId} />

      {/* Extras / Sonderwunsch / Verhandlung */}
      {(Array.isArray(order.selected_addons) && order.selected_addons.length > 0) || order.selected_wear_days ? (
        <div className="px-4 pb-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("seller_order_detail_extra.buyer_extras")}</p>
          <ul className="text-xs text-[#666] space-y-0.5 p-3 border border-[#E5E5E5] rounded-lg">
            {(order.selected_addons || []).map((a) => (
              <li key={a.code} data-testid={`seller-order-addon-${a.code}`} className="flex justify-between">
                <span data-testid={`seller-order-addon-label-${a.code}`}>{addonLabel(a.code, addonLabels[a.code] || a.code.replace(/^OPT_/, "").replaceAll("_", " ").toLowerCase())}</span>
                <span className="tabular-nums">+{Number(a.price).toFixed(2)} {currency}</span>
              </li>
            ))}
            {order.selected_wear_days && (
              <li data-testid={`seller-order-wear-${order.selected_wear_days.days}`} className="flex justify-between">
                <span>+{order.selected_wear_days.days} Tag{order.selected_wear_days.days === 1 ? "" : "e"} zusätzlich tragen</span>
                <span className="tabular-nums">+{Number(order.selected_wear_days.price).toFixed(2)} {currency}</span>
              </li>
            )}
          </ul>
        </div>
      ) : null}

      {/* Digitaler Content (Tragefotos/-videos) Upload + Freigabe für den Käufer */}
      <OrderContentDeliverySeller orderId={orderId} />

      {order.special_request && (
        <div className="px-4 pb-4" data-testid="seller-order-special-request">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("seller_order_detail_extra.buyer_wish")}</p>
          <div className="p-3 border border-amber-300 bg-amber-50 rounded-lg space-y-1">
            <p className="text-sm text-amber-900 whitespace-pre-line">&bdquo;{order.special_request}&ldquo;</p>
            {order.negotiated_price != null && (
              <p className="text-[11px] text-amber-800">{t("seller_order_detail_extra.agreed_price")} <strong>{Number(order.negotiated_price).toFixed(2)} {currency}</strong></p>
            )}
          </div>
        </div>
      )}

      {/* Status-Übersicht (identisch zu Käufer) */}
      <div className="px-4 pb-4" data-testid="seller-order-status-card">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">Status</p>
        <div className="p-3 border border-[#E5E5E5] rounded-lg space-y-1.5 bg-white">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#666]">Bestellstatus</span>
            <span className="text-xs font-semibold text-[#0A0A0A]">{STATUS_LABEL[order.status] || order.status}</span>
          </div>
          {(() => {
            const st = order.status;
            const isPostPaid = ["PAID","AWAITING_INBOUND","INBOUND_RECEIVED","SHIPPED","DELIVERED","RECEIVED","COMPLETED"].includes(st);
            const rawPayment = order.payment_status;
            const isPending = !isPostPaid && rawPayment === "PAYMENT_PENDING";
            const isFailed = rawPayment === "FAILED" || rawPayment === "REFUNDED";
            return (
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#666]">{t("seller_order_detail_extra.payment")}</span>
                <span className={`text-xs ${isFailed ? "text-red-600 font-semibold" : isPending ? "text-amber-700" : "text-emerald-700 font-medium"}`}>
                  {isFailed ? (rawPayment === "REFUNDED" ? "Erstattet" : "Fehlgeschlagen")
                    : isPending ? "Ausstehend"
                    : "Bezahlt"}
                </span>
              </div>
            );
          })()}
          {order.shipping_mode && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#666]">Versandart</span>
              <span className="text-xs text-[#0A0A0A]">{isHubShipping ? "Anonymer Hub-Versand" : "Anonymer Direktversand"}</span>
            </div>
          )}
          {order.shipped_at && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#666]">Versendet am</span>
              <span className="text-xs text-[#0A0A0A]">{new Date(order.shipped_at).toLocaleDateString("de-DE")}</span>
            </div>
          )}
          {order.delivered_at && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#666]">Zugestellt am</span>
              <span className="text-xs text-[#0A0A0A]">{new Date(order.delivered_at).toLocaleDateString("de-DE")}</span>
            </div>
          )}
        </div>
      </div>

      {/* Versandstatus mit Progress-Steps */}
      <div className="px-4 pb-4" data-testid="seller-order-shipping-progress">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">Versandstatus</p>
        <div className="p-3 border border-[#E5E5E5] rounded-lg bg-white space-y-3">
          {(() => {
            const status = order.status;
            const midLabel = isHubShipping ? "Ans Hub" : "Vorbereitet";
            const steps = [
              { key: "PAID", label: "Bezahlt", done: ["PAID","AWAITING_INBOUND","INBOUND_RECEIVED","SHIPPED","DELIVERED","RECEIVED","COMPLETED"].includes(status), ts: order.paid_at || order.created_at },
              { key: "AWAITING_INBOUND", label: midLabel, done: ["AWAITING_INBOUND","INBOUND_RECEIVED","SHIPPED","DELIVERED","RECEIVED","COMPLETED"].includes(status), ts: order.inbound_shipped_at || null },
              { key: "SHIPPED", label: isHubShipping ? "Hub → Käufer" : "Versendet", done: ["SHIPPED","DELIVERED","RECEIVED","COMPLETED"].includes(status), ts: order.shipped_at },
              { key: "DELIVERED", label: "Zugestellt", done: ["DELIVERED","RECEIVED","COMPLETED"].includes(status), ts: order.delivered_at },
            ];
            return (
              <div className="flex items-center justify-between gap-1">
                {steps.map((step, idx) => (
                  <div key={step.key} className="flex-1 min-w-0" data-testid={`seller-shipping-step-${step.key}`}>
                    <div className="flex items-center gap-1">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${step.done ? "bg-emerald-600 text-white" : "bg-[#F5F5F5] text-[#CCC] border border-[#E5E5E5]"}`}>
                        {step.done ? "✓" : idx + 1}
                      </div>
                      {idx < steps.length - 1 && (
                        <div className={`flex-1 h-0.5 ${steps[idx + 1].done ? "bg-emerald-600" : "bg-[#E5E5E5]"}`} />
                      )}
                    </div>
                    <p className={`mt-1 text-[10px] leading-tight ${step.done ? "text-[#0A0A0A] font-medium" : "text-[#999]"}`}>{step.label}</p>
                    {step.ts && step.done && (
                      <p className="text-[9px] text-[#999]">{new Date(step.ts).toLocaleDateString("de-DE")}</p>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
          {order.status === "PAID" && !order.shipped_at && (
            <p className="text-[11px] text-amber-700 pt-2 border-t border-[#F0F0F0]">
              {isHubShipping
                ? "Käufer hat bezahlt — bitte Etikett + Beileger drucken und das Paket ans Hub schicken."
                : "Käufer hat bezahlt — bitte Paket ans Käuferadresse versenden und Sendungsnummer eintragen."}
            </p>
          )}
          {order.status === "AWAITING_INBOUND" && (
            <p className="text-[11px] text-blue-700 pt-2 border-t border-[#F0F0F0]">
              Deine Sendung ist unterwegs zum stilltouched-Hub. Sobald sie geprüft und weiterversendet wurde, siehst du hier den nächsten Schritt.
            </p>
          )}
          {order.status === "SHIPPED" && !order.delivered_at && (
            <p className="text-[11px] text-emerald-700 pt-2 border-t border-[#F0F0F0]">
              {isHubShipping ? "Das Hub hat das Paket an den Käufer weiterversendet." : "Paket ist auf dem Weg zum Käufer."}
            </p>
          )}

          {/* Tracking-Info (Sendungsnummer + Verfolgungs-Link) mit Inline-Edit */}
          <div className="pt-2 border-t border-[#F0F0F0] space-y-2">
            {(() => {
              // STX: Seller sieht inbound_* Felder (Weg zum Hub, Buyer sieht das nie)
              // MP:  Seller sieht tracking_* Felder (Weg zum Buyer)
              const trkCarrier = isHubShipping ? order.inbound_tracking_carrier : order.tracking_carrier;
              const trkNumber = isHubShipping ? order.inbound_tracking_number : order.tracking_number;
              const trkUrl = isHubShipping ? order.inbound_tracking_url : order.tracking_url;
              const hasTracking = !!(trkCarrier || trkNumber || trkUrl);
              const headerLabel = isHubShipping ? "Sendung ans Hub" : "Sendungsverfolgung";
              return (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] uppercase tracking-wider text-[#999] font-medium">{headerLabel}</p>
                    {!editTracking && (
                      <button
                        data-testid="seller-tracking-edit-btn"
                        onClick={startEditTracking}
                        className="text-[10px] text-[#666] hover:text-[#0A0A0A] inline-flex items-center gap-1"
                      >
                        <Edit3 size={10} /> {trkNumber ? "Bearbeiten" : "Sendungsnummer eintragen"}
                      </button>
                    )}
                  </div>

                  {isHubShipping && !editTracking && (
                    <p className="text-[10px] text-[#666] bg-blue-50 border border-blue-200 rounded p-1.5">
                      <strong>Nur intern sichtbar:</strong> {t("seller_order_detail_extra.tracks_path")} <em>zum stilltouched-Hub</em>. Der Käufer sieht sie nicht — er bekommt später eine neue Nummer, wenn das Hub das Paket weiterversendet.
                    </p>
                  )}

                  {editTracking ? (
                    <div className="space-y-2 bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-2">
                      <div>
                        <label className="text-[10px] text-[#666] block mb-0.5">Carrier</label>
                        <select
                          data-testid="seller-tracking-carrier-select"
                          value={trackForm.tracking_carrier}
                          onChange={(e) => setTrackForm((f) => ({ ...f, tracking_carrier: e.target.value }))}
                          className="w-full h-9 px-2 border border-[#E5E5E5] bg-white text-sm rounded"
                        >
                          <option value="">{t("seller_order_detail_extra.please_choose")}</option>
                          <option value="DHL">DHL</option>
                          <option value="DEUTSCHE_POST">Deutsche Post</option>
                          <option value="HERMES">Hermes</option>
                          <option value="DPD">DPD</option>
                          <option value="UPS">UPS</option>
                          <option value="GLS">GLS</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-[#666] block mb-0.5">
                          {isHubShipping ? "Sendungsnummer zum Hub" : "Sendungsnummer"}
                        </label>
                        <input
                          data-testid="seller-tracking-number-input"
                          type="text"
                          value={trackForm.tracking_number}
                          onChange={(e) => setTrackForm((f) => ({ ...f, tracking_number: e.target.value }))}
                          placeholder="z. B. 35.764.529397 0"
                          className="w-full h-9 px-2 border border-[#E5E5E5] bg-white text-sm rounded font-mono"
                        />
                        <p className="text-[9px] text-[#999] mt-0.5">Punkte / Leerzeichen werden automatisch entfernt.</p>
                      </div>
                      {isHubShipping ? (
                        order.status === "PAID" && trackForm.tracking_number && (
                          <p className="text-[10px] text-blue-800 bg-blue-50 border border-blue-200 rounded p-1.5">
                            Beim Speichern wird der Status auf &bdquo;Wartet auf Hub-Eingang&ldquo; gesetzt. Der Käufer sieht die Nummer NICHT — er bekommt später eine neue Sendungsnummer aus dem Hub.
                          </p>
                        )
                      ) : (
                        order.status === "PAID" && trackForm.tracking_number && (
                          <p className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-1.5">
                            Beim Speichern wird die Bestellung automatisch auf &bdquo;Versendet&ldquo; gesetzt und der Käufer benachrichtigt.
                          </p>
                        )
                      )}
                      <div className="flex gap-1.5">
                        <button
                          data-testid="seller-tracking-save"
                          onClick={saveTracking}
                          disabled={busy === "tracking"}
                          className="flex-1 h-9 rounded bg-[#0A0A0A] text-white text-xs font-medium hover:bg-[#222] disabled:opacity-50 inline-flex items-center justify-center gap-1"
                        >
                          <Save size={11} /> {busy === "tracking" ? "..." : "Speichern"}
                        </button>
                        <button
                          data-testid="seller-tracking-cancel"
                          onClick={() => { setEditTracking(false); setError(""); }}
                          className="flex-1 h-9 rounded border border-[#E5E5E5] text-xs text-[#666] hover:bg-white inline-flex items-center justify-center gap-1"
                        >
                          <X size={11} /> Abbrechen
                        </button>
                      </div>
                    </div>
                  ) : hasTracking ? (
                    <div className="flex flex-wrap gap-1.5 items-center">
                      {trkCarrier && (
                        <span data-testid="seller-tracking-carrier" className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-[10px] text-[#666]">
                          <Truck size={10} /> {trkCarrier}
                        </span>
                      )}
                      {trkNumber && (
                        <span data-testid="seller-tracking-number" className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-[10px] text-[#666] font-mono">
                          {trkNumber}
                        </span>
                      )}
                      {trkUrl && (
                        <a href={trkUrl} target="_blank" rel="noopener noreferrer"
                          data-testid="seller-tracking-link"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#0A0A0A] text-white text-[10px] hover:bg-[#222]">
                          Sendung verfolgen <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                  ) : (
                    <p className="text-[11px] text-[#999]">{t("seller_order_detail_extra.no_tracking_yet")}</p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Versand & Aktionen */}
      <div className="px-4 pb-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("seller_order_detail_extra.shipping_actions")}</p>
        <div className="p-3 border border-[#E5E5E5] rounded-lg space-y-3">
          <div className="flex items-center gap-2 text-xs text-[#666]">
            <Truck size={14} />
            <span data-testid="seller-order-shipping-mode">
              {isHubShipping ? "Anonymer Hub-Versand" : "Anonymer Direktversand (via Dienstleister)"}
            </span>
          </div>

          {error && (
            <InfoCard variant="danger">
              <AlertTriangle size={12} /> {error}
            </InfoCard>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              data-testid="seller-order-label-btn"
              onClick={() => isHubShipping && openPdf("shipping-label", "stx-label")}
              disabled={!isHubShipping || busy === "shipping-label"}
              className={`h-11 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                isHubShipping
                  ? "bg-[#0A0A0A] text-white hover:bg-[#222] active:scale-[0.98]"
                  : "bg-[#F5F5F5] text-[#999] cursor-not-allowed border border-[#E5E5E5]"
              }`}
            >
              <FileText size={14} />
              {busy === "shipping-label" ? "..." : "Versandetikett generieren"}
              {!isHubShipping && <span className="text-[9px] uppercase ml-1">Coming Soon</span>}
            </button>

            <button
              data-testid="seller-order-insert-btn"
              onClick={() => isHubShipping && openPdf("parcel-insert", "stx-insert")}
              disabled={!isHubShipping || busy === "parcel-insert"}
              className={`h-11 rounded-lg text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                isHubShipping
                  ? "bg-white border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F5F5F5] active:scale-[0.98]"
                  : "bg-[#F5F5F5] text-[#999] cursor-not-allowed border border-[#E5E5E5]"
              }`}
            >
              <QrCode size={14} />
              {busy === "parcel-insert" ? "..." : "QR-Beileger generieren"}
              {!isHubShipping && <span className="text-[9px] uppercase ml-1">Coming Soon</span>}
            </button>
          </div>

          {!isHubShipping && (
            <p className="text-[10px] text-[#999] flex items-start gap-1">
              <Clock size={10} className="mt-0.5 shrink-0" />
              Der anonyme Direktversand über Dienstleister-API ist bald verfügbar. Aktuell nur Hub-Versand aktiv.
            </p>
          )}

          {order.status === "PAID" && (
            <button
              data-testid="seller-order-mark-shipped"
              onClick={markShipped}
              disabled={busy === "ship"}
              className="w-full h-11 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              <CheckCircle size={14} />
              {busy === "ship" ? "..." : "Ich habe versendet"}
            </button>
          )}

          {order.status === "SHIPPED" && (
            <InfoCard variant="success">
              <CheckCircle size={14} /> Als versendet markiert
            </InfoCard>
          )}
        </div>
      </div>

      {/* Lieferadresse (nur bei Direktversand relevant, sonst Hub) */}
      {(order.shipping_address || order.shipping_name || order.packstation_number) && !isHubShipping && (
        <div className="px-4 pb-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">Lieferadresse</p>
          <div className="p-3 border border-[#E5E5E5] rounded-lg text-xs text-[#0A0A0A] space-y-0.5 flex gap-2">
            <MapPin size={14} className="text-[#999] mt-0.5 shrink-0" />
            <div>
              {order.shipping_name && <p><strong>{order.shipping_name}</strong></p>}
              {order.packstation_number ? (
                <p>Packstation {order.packstation_number}{order.post_number ? ` · PostNr ${order.post_number}` : ""}</p>
              ) : null}
              {order.shipping_address && <p>{order.shipping_address}</p>}
              {(order.shipping_zip || order.shipping_city) && <p>{[order.shipping_zip, order.shipping_city].filter(Boolean).join(" ")}</p>}
              {order.shipping_country && <p>{order.shipping_country}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Chat */}
      {["PAID", "SHIPPED", "DELIVERED", "RECEIVED", "COMPLETED"].includes(order.status) && (
        <div className="px-4 pb-4">
          <button
            data-testid="seller-order-chat-btn"
            onClick={() => navigate(`/orders/${order.order_id}/chat`)}
            className="w-full h-11 rounded-lg border border-[#E5E5E5] text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors flex items-center justify-center gap-2">
            <MessageCircle size={14} /> Chat mit Käufer
          </button>
        </div>
      )}
    </div>
  );
}
